import { useState, useCallback } from 'react';
import { useAntiSpam } from './useAntiSpam';
import { generateQuestion, DifficultyLevel } from '../utils/advancedMultiplication';

interface Question {
  question: string;
  answer: number;
  explanation?: string;
}

interface GameStats {
  viewed: number;
  skipped: number;
  total: number;
}

export const useAdvancedGameLogic = () => {
  const [level, setLevel] = useState<DifficultyLevel>('beginner');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stats, setStats] = useState<GameStats>({
    viewed: 0,
    skipped: 0,
    total: 0,
  });

  const { checkAction, isBlocked } = useAntiSpam({
    minDelay: 300,
    burstLimit: 5,
    burstWindow: 3000,
    cooldownTime: 1500,
  });

  const addNewQuestion = useCallback(() => {
    setQuestions(prev => [...prev, generateQuestion(level)]);
  }, [level]);

  const setDifficultyLevel = (newLevel: DifficultyLevel) => {
    setLevel(newLevel);
    setQuestions([generateQuestion(newLevel)]);
    setCurrentIndex(0);
    setShowAnswer(false);
    setStats({ viewed: 0, skipped: 0, total: 0 });
  };

  const skipQuestion = () => {
    if (!checkAction()) return;

    setStats(prev => ({ ...prev, skipped: prev.skipped + 1, total: prev.total + 1 }));
    setShowAnswer(false);
    if (currentIndex === questions.length - 1) {
      addNewQuestion();
    }
    setCurrentIndex(prev => prev + 1);
  };

  const previousQuestion = () => {
    if (!checkAction()) return;

    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const nextQuestion = () => {
    if (!checkAction()) return;

    if (!showAnswer) {
      setShowAnswer(true);
      setStats(prev => ({ ...prev, viewed: prev.viewed + 1, total: prev.total + 1 }));
    } else {
      setShowAnswer(false);
      if (currentIndex === questions.length - 1) {
        addNewQuestion();
      }
      setCurrentIndex(prev => prev + 1);
    }
  };

  // Initialize first question if needed
  if (questions.length === 0) {
    const firstQuestion = generateQuestion(level);
    setQuestions([firstQuestion]);
  }

  return {
    level,
    setDifficultyLevel,
    currentQuestion: questions[currentIndex] || generateQuestion(level),
    showAnswer,
    skipQuestion,
    previousQuestion,
    nextQuestion,
    stats,
    isBlocked,
  };
};